import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-[#0F2341] text-white hover:bg-[#41A0DC] shadow-sm transition-all duration-300",
        secondary: "bg-transparent text-[#0F2341] border border-[#0F2341] hover:text-[#41A0DC] hover:border-[#41A0DC] transition-all duration-300",
        tertiary: "bg-[#41A0DC] text-[#0F2341] hover:bg-[#0F2341] hover:text-white shadow-sm transition-all duration-300",
        third: "bg-[#41A0DC] text-[#0F2341] border border-[#41A0DC] hover:bg-[#0F2341] hover:border-[#0F2341] hover:text-white shadow-sm transition-all duration-300",
        accent: "bg-[#FFB40F] text-[#0F2341] hover:bg-[#FFB40F]/90 shadow-sm transition-all duration-300",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-card-border bg-background hover:bg-secondary hover:text-secondary-foreground shadow-card hover:shadow-elevated",
        ghost: "hover:bg-accent/10 hover:text-accent",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-[#0F2341] text-white hover:bg-[#0F2341]/90 shadow-hero hover:shadow-elevated transform hover:-translate-y-1 font-semibold",
        trust: "bg-[#0F2341] text-white hover:bg-[#0F2341]/90 shadow-hero hover:shadow-elevated transform hover:-translate-y-0.5 font-semibold",
        corporate: "bg-card border border-card-border text-card-foreground hover:bg-primary hover:text-primary-foreground shadow-card hover:shadow-elevated transition-all duration-300",
      },
      size: {
        default: "h-10 px-2 py-1",
        sm: "h-8 rounded-lg px-2 text-xs",
        lg: "h-12 rounded-lg px-4 text-base font-semibold",
        xl: "h-14 rounded-lg px-5 text-lg font-semibold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
